import { getDefaultLevel, type LevelDef } from './levels.js';
import { makePatrol, parseMap } from './map.js';
import type { Guard, GuardTag, Vec2, World } from './types.js';
import { STATE } from './types.js';

interface GuardSpawnConfig {
  tag: GuardTag;
  fallback: Vec2;
}

const GUARD_SPAWNS: ReadonlyArray<GuardSpawnConfig> = [
  { tag: 'g', fallback: { x: 160, y: 0 } },
  { tag: 'h', fallback: { x: 0, y: 120 } },
  { tag: 'i', fallback: { x: 0, y: 80 } },
];

function makeGuard(path: Vec2[], id: number): Guard {
  const first = path[0]!;
  return {
    id,
    x: first.x,
    y: first.y,
    dir: 0,
    speed: 50,
    vision: 150,
    fov: Math.PI / 2.4,
    state: 'patrol',
    alert: 0,
    path,
    pi: 1,
    target: null,
    lastSeen: null,
    idleT: 0,
    ko: false,
    dragged: false,
    hiddenInBox: false,
    navPath: null,
    navIdx: 0,
    navTargetTile: null,
    navStuckT: 0,
    walkT: 0,
    currentSpeed: 0,
  };
}

/**
 * Build a World from a level definition. Backwards-compatible: passing a
 * raw map string array still works (treated as an anonymous level), but
 * production code should pass a LevelDef so persistence keys and target
 * times come from a single source of truth.
 */
export function createWorld(levelOrMap: LevelDef | readonly string[] = getDefaultLevel()): World {
  const level: LevelDef = Array.isArray(levelOrMap)
    ? { id: 'anon', name: 'anon', description: '', map: levelOrMap, targetTime: 90 }
    : (levelOrMap as LevelDef);
  const m = parseMap(level.map);

  const guards: Guard[] = [];
  for (const { tag, fallback } of GUARD_SPAWNS) {
    const path = makePatrol(m.guardSpawns[tag] ?? [], fallback);
    if (path) guards.push(makeGuard(path, guards.length + 1));
  }

  return {
    ...m,
    player: {
      x: m.playerStart.x,
      y: m.playerStart.y,
      r: 11,
      dir: 0,
      vx: 0,
      vy: 0,
      inBox: false,
      carryingBody: null,
      hp: 1,
      walkT: 0,
    },
    guards,
    activeBox: null,
    decoys: [],
    sounds: [],
    particles: [],
    cooldowns: { box: 0, knock: 0, decoy: 0, takedown: 0, hack: 0 },
    state: STATE.PLAY,
    time: 0,
    levelId: level.id,
    targetTime: level.targetTime,
    stats: makeStats(),
    collectibles: m.collectibleSpawns.map((p) => ({ x: p.x, y: p.y, picked: false, pickupT: 0 })),
    cameras: m.cameraSpawns.map((s) => ({
      x: s.pos.x,
      y: s.pos.y,
      baseDir: s.baseDir,
      panAmplitude: Math.PI / 3,
      panSpeed: 0.7,
      panT: 0,
      fov: Math.PI / 3,
      vision: 130,
      hackT: 0,
    })),
    objective: level.objective ?? { kind: 'extract' },
  };
}

function makeStats(): import('./types.js').Stats {
  return {
    detected: false,
    kos: 0,
    bodiesHidden: 0,
    decoysUsed: 0,
    knocksUsed: 0,
  };
}

export function resetWorld(world: World): void {
  world.player.x = world.playerStart.x;
  world.player.y = world.playerStart.y;
  world.player.dir = 0;
  world.player.inBox = false;
  world.player.carryingBody = null;
  world.player.vx = 0;
  world.player.vy = 0;
  world.player.walkT = 0;

  for (const g of world.guards) {
    const first = g.path[0]!;
    g.ko = false;
    g.hiddenInBox = false;
    g.alert = 0;
    g.state = 'patrol';
    g.target = null;
    g.lastSeen = null;
    g.idleT = 0;
    g.x = first.x;
    g.y = first.y;
    g.pi = 1;
    g.navPath = null;
    g.navIdx = 0;
    g.navTargetTile = null;
    g.navStuckT = 0;
    g.walkT = 0;
  }

  world.activeBox = null;
  world.decoys.length = 0;
  world.sounds.length = 0;
  world.particles.length = 0;
  for (const k of Object.keys(world.cooldowns) as (keyof World['cooldowns'])[]) {
    world.cooldowns[k] = 0;
  }
  world.state = STATE.PLAY;
  world.time = 0;
  // Stats are reset in-place to keep the same reference (HUD reads it).
  world.stats.detected = false;
  world.stats.kos = 0;
  world.stats.bodiesHidden = 0;
  world.stats.decoysUsed = 0;
  world.stats.knocksUsed = 0;
  for (const c of world.collectibles) {
    c.picked = false;
    c.pickupT = 0;
  }
  for (const cam of world.cameras) {
    cam.panT = 0;
    cam.hackT = 0;
  }
}
